/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { MainContext, NO_CACHE, handleError, useReportedQuery } from 'openvsx-webui';
import type { UsageStats } from '../rate-limit-types';
import { getDefaultStartDate } from '../components/usage-stats/usage-stats-utils';
import { getUserCustomers, getUserUsageStats } from './user-customers-service';

/** Loads the customers the signed-in user is a member of; shared by the tab's gate and its content. */
export const useUserCustomers = () => {
  const { service, user } = useContext(MainContext);
  return useReportedQuery(
    useQuery({
      queryKey: ['user', 'customers'],
      queryFn: ({ signal }) => getUserCustomers(service.serverUrl, signal),
      enabled: user != null,
      ...NO_CACHE
    }),
    { action: 'load your rate limiting customers' }
  );
};

/** Whether the Rate Limiting tab applies: only while the user is in at least one customer. */
export const useHasUserCustomers = (): boolean => (useUserCustomers().data ?? []).length > 0;

// Stable empty reference so consumers don't see a new array identity on every render.
const NO_STATS: readonly UsageStats[] = [];

export const useUserUsageStats = (customerName: string) => {
  const { service } = useContext(MainContext);
  const [startDate, setStartDate] = useState<DateTime>(getDefaultStartDate);

  const { data, error } = useQuery({
    queryKey: ['user', 'customers', customerName, 'usage', startDate.toMillis()],
    queryFn: ({ signal }) => getUserUsageStats(service.serverUrl, signal, customerName, startDate.toJSDate())
  });

  return {
    usageStats: data?.stats ?? NO_STATS,
    dailyP95: data?.dailyP95,
    error: error ? handleError(error as Error) : null,
    startDate,
    setStartDate
  };
};
